import { action } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import type { CardGoogleClassroom, GoogleClassroomPostType } from "@shared/models/card-stack"
import type { GoogleClassTopic } from "@shared/models/google-classroom"
import type {
  GoogleClassroomCardEditor,
  GoogleClassroomTextProp,
} from "cc-frontend/lib/google-classroom/card-editor"
import type EmberStore from "cc-frontend/services/ember-store"
import type Session from "cc-frontend/services/session"
import { defaultMatcher } from "ember-power-select/utils/group-utils"
import { find, isNil } from "lodash-es"
import styles from "./text-input.module.css"

interface Signature {
  Args: {
    postType: GoogleClassroomPostType
    cardEditor: GoogleClassroomCardEditor
    card: CardGoogleClassroom
    googleClassTopics: Array<GoogleClassTopic> | null
    cannotEditMultipleChoiceItems: boolean
  }
}

interface VisibleFormElements {
  pointsField: VisibleFormElement
  topicField: VisibleFormElement
  titleField: VisibleFormElement
  valueField: VisibleFormElement
  multipleChoiceOptions?: VisibleFormElement
}

interface VisibleFormElement {
  isVisible: boolean
  placeholder?: string
  label?: string
}

export default class GoogleClassroomTextComponent extends Component<Signature> {
  readonly styles = styles
  @service("ember-store") declare store: EmberStore
  @service declare session: Session

  get visibleFormElements(): VisibleFormElements {
    if (this.args.postType === undefined)
      return {
        pointsField: { isVisible: false },
        titleField: { isVisible: false },
        valueField: { isVisible: false },
        topicField: { isVisible: false },
      }
    // HACK: We handle null here because we have some old cards. We need to fix that on the DB
    else if (this.args.postType === "ASSIGNMENT" || this.args.postType === null) {
      return {
        pointsField: { isVisible: true },
        topicField: { isVisible: true },
        titleField: { isVisible: true, placeholder: "Title", label: "Title" },
        valueField: { isVisible: true, placeholder: "Instructions", label: "Instructions" },
      }
    } else if (this.args.postType === "MATERIAL") {
      return {
        pointsField: { isVisible: false },
        topicField: { isVisible: true },
        titleField: { isVisible: true, placeholder: "Title", label: "Title" },
        valueField: {
          isVisible: true,
          placeholder: "Description (optional)",
          label: "Description",
        },
      }
    } else if (this.args.postType === "ANNOUNCEMENT") {
      return {
        pointsField: { isVisible: false },
        topicField: { isVisible: false },
        titleField: { isVisible: false },
        valueField: {
          isVisible: true,
          placeholder: "Announce something to your class",
          label: "Announcement",
        },
      }
    } else if (this.args.postType === "SHORT_ANSWER_QUESTION") {
      return {
        pointsField: { isVisible: true },
        topicField: { isVisible: true },
        titleField: { isVisible: true, placeholder: "Question", label: "Question" },
        valueField: {
          isVisible: true,
          placeholder: "Instructions (Optional)",
          label: "Instructions",
        },
      }
    } else if (this.args.postType === "MULTIPLE_CHOICE_QUESTION") {
      return {
        pointsField: { isVisible: true },
        topicField: { isVisible: true },
        titleField: { isVisible: true, placeholder: "Question", label: "Question" },
        multipleChoiceOptions: { isVisible: true },
        valueField: {
          isVisible: true,
          placeholder: "Instructions (Optional)",
          label: "Instructions",
        },
      }
    } else {
      throw new Error(`Unsupported Post Type: ${this.args.postType}`)
    }
  }

  @action
  updateSection(prop: GoogleClassroomTextProp, value: string | null) {
    this.args.cardEditor.editProp(prop, value)
  }

  @action
  finishedEditing(
    prop: GoogleClassroomTextProp,
    value: string | null,
    originalValue?: string | null,
    wordCountChange?: number
  ) {
    this.args.cardEditor.finishEditingProp(prop, value, originalValue ?? null, wordCountChange || 0)
  }

  @action onTopicSelectOrCreate(topic) {
    this.args.cardEditor.setTopic(topic)
  }

  @action showCreateWhen(term) {
    // @ts-expect-error `topic` seems incorrect? Unless the old version of ember-power-select
    // allows non-strings.
    let res = isNil(find(this.args.googleClassTopics, (topic) => defaultMatcher(topic, term) >= 0))
    return res
  }

  reorderChoices = (originalChoices, newChoices) => {
    if (this.args.cannotEditMultipleChoiceItems) return
    this.args.cardEditor.setChoices(newChoices)
  }

  removeChoice = (index: number) => {
    if (this.args.cannotEditMultipleChoiceItems) return
    let choices = [...(this.args.card.attributes.extraPostProperties.choices || [])]
    choices.splice(index, 1)
    this.args.cardEditor.setChoices(choices)
  }

  updateChoice = (index, choice) => {
    if (this.args.cannotEditMultipleChoiceItems) return
    let choices = [...(this.args.card.attributes.extraPostProperties.choices || [])]
    choices[index] = choice
    this.args.cardEditor.setChoices(choices)
  }

  addChoice = () => {
    if (this.args.cannotEditMultipleChoiceItems) return
    let choices = this.args.card.attributes.extraPostProperties.choices || []
    choices = [...choices, `Option ${choices.length + 1}`]
    this.args.cardEditor.setChoices(choices)
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "Planner::GoogleClassroom::Parts::TextInput": typeof GoogleClassroomTextComponent
  }
}
